import { FC, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form } from "antd";
import { LMDropdown, LMNewInput, LMUpload } from "../../../components";
import {
  IProductFormValues,
  IProductCategory,
  IProduct,
  EProductTemperature,
  EProductType,
  EPickingType,
  EDisplaySize,
  EProductStatus,
} from "../types";
import { createCategory, deleteProductImages, updateProduct } from "../api";
import { AppDispatch } from "../../../app/store";
import { fetchCategories, refreshList } from "../slice";
import { IMetadataEntry } from "../../metadata/types";
import { LMNewButton } from "../../../components/LMNewButton";
import { mapProductToUpdate } from "./utils";
import { getTranslation, setTranslation } from "../../i18n";
import { IconPlus } from "../../../assets";
import { BarCodesSelector } from "../BarCodesSelector";
import styles from "./UpdateProductForm.module.css";

const NEW_CATEGORY_VALUE = "create-new-category";

interface Props {
  onClose: () => void;
  categories: IProductCategory[];
  metadataEntries: IMetadataEntry[];
  product: IProduct;
  onSuccess: () => void;
  onError: () => void;
  editable: boolean;
}

export const UpdateProductForm: FC<Props> = ({
  onClose,
  categories,
  metadataEntries,
  product,
  onSuccess,
  onError,
  editable,
}) => {
  const { t } = useTranslation("productsPortfolio");
  const { t: tLayout } = useTranslation("layout");
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<IProductFormValues>();
  const dispatch = useDispatch<AppDispatch>();

  const getInitialValues = useCallback(() => {
    const images = product.images;
    const lastImage = images[images.length - 1];
    return {
      name: getTranslation(product.name),
      brand: getTranslation(product.brand),
      description: getTranslation(product.description),
      type: product.type,
      status: product.status,
      displaySize: product.displaySize,
      temperature: product.temperature,
      pickingType: product.pickingType,
      categoryId: product.categoryId ? product.categoryId.toString() : undefined,
      heightMm: product.heightMm || undefined,
      weightKg: product.weightKg || undefined,
      volumeL: product.volumeL || undefined,
      lengthMm: product.lengthMm || undefined,
      widthMm: product.widthMm || undefined,
      image: lastImage ? lastImage.url : undefined,
      barcodes: product.barcodes ? product.barcodes : [],
    };
  }, [product, metadataEntries]);

  const onFinish = async (values: IProductFormValues) => {
    setLoading(true);
    try {
      if (values.categoryName) {
        const category = await createCategory({
          name: setTranslation(values.categoryName),
        });
        await updateProduct(
          product.id,
          mapProductToUpdate(
            {
              ...values,
              categoryId: category.data.id.toString(),
            },
            product
          )
        );
        await dispatch(fetchCategories());
      } else {
        await updateProduct(product.id, mapProductToUpdate(values, product));
      }
      await dispatch(refreshList());
      form.resetFields();
      setLoading(false);
      onSuccess();
      onClose();
    } catch (e: any) {
      onError();
      setLoading(false);
    }
  };

  const categoriesItems = categories.map((category) => ({
    name: getTranslation(category.name),
    value: category.id.toString(),
  }));

  const addCategoryItem = {
    name: t("createCategory"),
    value: NEW_CATEGORY_VALUE,
    icon: (
      <div className={styles["create-category-icon"]}>
        <IconPlus stroke="var(--color-primary-80)" />
      </div>
    ),
  };

  const category = Form.useWatch("categoryId", form);

  const handleSubmit = () => form.submit();

  const handleImageClear = () => {
    deleteProductImages(product.id, product.images);
    form.setFieldValue("image", "");
  };

  return (
    <div className={styles.container}>
      <Form
        layout={"vertical"}
        form={form}
        className={styles.form}
        initialValues={getInitialValues()}
        onValuesChange={() => setDirty(true)}
        onFinish={onFinish}
        autoComplete="off"
      >
        <div className={styles.fields}>
          <Form.Item
            name="name"
            label={t("productName")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMNewInput
              placeholder={t("namePlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="status" label={t("productStatus")}>
            <LMDropdown
              placeholder={t("productStatus")}
              disabled={!editable}
              items={Object.values(EProductStatus).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label={t("productDescription")}>
            <LMNewInput
              placeholder={t("descriptionPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="brand" label={t("brand")}>
            <LMNewInput
              placeholder={t("brandPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="image" label={t("productImage")}>
            <LMUpload
              image={form.getFieldValue("image")}
              onClear={handleImageClear}
            />
          </Form.Item>
          <Form.Item name="displaySize" label={t("displaySize")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              disabled={!editable}
              items={Object.values(EDisplaySize).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item name="categoryId" label={t("category")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={[addCategoryItem, ...categoriesItems]}
              disabled={!editable}
            />
          </Form.Item>
          {category === NEW_CATEGORY_VALUE && (
            <Form.Item name="categoryName" label={t("categoryName")}>
              <LMNewInput
                placeholder={t("categoryNamePlaceholder")}
                disabled={!editable}
              />
            </Form.Item>
          )}
          <Form.Item name="type" label={t("productType")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              disabled={!editable}
              items={Object.values(EProductType).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item name="pickingType" label={t("productPickingType")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              disabled={!editable}
              items={Object.values(EPickingType).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="temperature"
            label={t("temperature")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              disabled={!editable}
              items={Object.values(EProductTemperature).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item name="barcodes" label="Barcodes">
            <BarCodesSelector />
          </Form.Item>
          <Form.Item name="heightMm" label={t("heightMm")}>
            <LMNewInput
              type="number"
              placeholder={t("heightMmPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="widthMm" label={t("widthMm")}>
            <LMNewInput
              type="number"
              placeholder={t("widthMmPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="lengthMm" label={t("lengthMm")}>
            <LMNewInput
              type="number"
              placeholder={t("lengthMmPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="weightKg" label={t("weightKg")}>
            <LMNewInput
              type="number"
              placeholder={t("weightKgPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          <Form.Item name="volumeL" label={t("volumeL")}>
            <LMNewInput
              type="number"
              placeholder={t("volumeLPlaceholder")}
              disabled={!editable}
            />
          </Form.Item>
          {/* <Form.Item name="external" valuePropName="checked">
            <div className={styles["check-container"]}>
              <LMCheck disabled={!editable} />{" "}
              <Button2 className={styles["check-label"]}>
                {t("external")}
              </Button2>
            </div>
          </Form.Item> */}
          {/* <Form.Item
            name="nutrition"
            label={t("nutrition")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMNewInput
              placeholder={t("nutritionPlaceholder")}
              disabled={!editable}
            />
          </Form.Item> */}
        </div>
        {/* <MetadataFields
          withEdit
          entries={metadataEntries}
          disabled={!editable}
        /> */}
        {editable && (
          <div className={styles.buttons}>
            <LMNewButton
              color="default"
              onClick={onClose}
              className={styles.button}
            >
              {t("cancel")}
            </LMNewButton>

            <LMNewButton disabled={!dirty || loading} onClick={handleSubmit}>
              {loading ? tLayout("pleaseWait") : t("saveChanges")}
            </LMNewButton>
          </div>
        )}
      </Form>
    </div>
  );
};
